import * as React from 'react';
import {
    List,
    TextField,
    useTranslate,
    DatagridConfigurable,
    TopToolbar,
    SelectColumnsButton, ExportButton,
    useStore, downloadCSV, FunctionField,
    DateField, FilterButton, BooleanField
} from 'react-admin';
import {useMediaQuery, Theme} from '@mui/material';

import {useDefineAppLocation} from '@react-admin/ra-navigation';
import {OrderReturn} from "../../types";
import jsonExport from 'jsonexport/dist';
import orderReturnsFilters from './orderReturnsFilters';
import StatusChip from "./StatusChip";
import {SOURCE} from "../OrderList";
import {returnStates} from './returnStates';

const OrderReturnListActions = () => {
    return (
        <TopToolbar>
            <FilterButton/>
            <SelectColumnsButton preferenceKey="order.return.list"/>
            <ExportButton maxResults={10000}/>
        </TopToolbar>
    );
};

const OrderReturnList = () => {
    useDefineAppLocation('sales.order_returns');
    const t = useTranslate();

    const isSmall = useMediaQuery<Theme>(theme => theme.breakpoints.down('md'));

    let [availableColumns] = useStore('preferences.order.return.list.availableColumns');
    let [selectedColumns] = useStore('preferences.order.return.list.columns');

    const exporter = (returns: OrderReturn[]) => {
        const forExport = returns.map(orderReturn => {
            if (selectedColumns !== undefined) {
                let orderReturnsToExport = {};

                for (let i = 0; i < selectedColumns.length; i++) {
                    const column = availableColumns.find((column) => column.index === selectedColumns[i]);
                    if (column) {
                        if (column.source === 'status') {
                            const status = returnStates[orderReturn[column.source]];
                            orderReturnsToExport[t(`resources.order.return.fields.${column.source}`)] = t(status.translationKey);
                        } else if (column.source === 'source') {
                            orderReturnsToExport[t(`resources.order.return.fields.${column.source}`)] = SOURCE[orderReturn[column.source]];
                        } else if (column.source === 'reason') {
                            orderReturnsToExport[t(`resources.order.return.items.reasonDefaultDescription`)] = orderReturn[column.source];
                        } else {
                            orderReturnsToExport[t(`resources.order.return.fields.${column.source}`)] = orderReturn[column.source];
                        }
                    }
                }

                return orderReturnsToExport;
            }

            return {
                [t(`resources.order.return.fields.orderNumber`)]: orderReturn.orderNumber,
                [t(`resources.order.return.fields.orderDate`)]: orderReturn.orderDate,
                [t(`resources.order.return.fields.source`)]: orderReturn.source,
                [t(`resources.order.return.fields.number`)]: orderReturn.number,
                [t(`resources.order.return.fields.createdAt`)]: orderReturn.createdAt,
                [t(`resources.order.return.fields.status`)]: orderReturn.status,
                [t(`resources.order.return.fields.isFullReturn`)]: orderReturn.isFullReturn,
                [t(`resources.order.return.fields.itemsToReturn`)]: orderReturn.itemsToReturn
            };
        });
        jsonExport(forExport, {}, (err, csv) => {
            downloadCSV(csv, 'order_return_list');
        });
    };
    const filters: JSX.Element[] = orderReturnsFilters();

    return (
        <List
            exporter={exporter}
            filters={filters}
            perPage={25}
            actions={<OrderReturnListActions/>}
            sx={{marginTop: isSmall ? undefined : 1}}
        >
            <DatagridConfigurable
                omit={[
                    'carrier', 'returnDate', 'selectLineDocumentId', 'shippingReturn', 'reason'
                ]}
                preferenceKey="order.return.list"
                bulkActionButtons={false}
                rowClick="show"
            >
                <TextField source="orderNumber" label={`resources.order.return.fields.orderNumber`} sortable={false}/>
                <DateField
                    source="orderDate"
                    label={`resources.order.return.fields.orderDate`}
                    locales="de-DE"
                    options={{
                        weekday: undefined,
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                    }}
                    sortable={false}
                />
                <FunctionField
                    source="source"
                    label={`resources.order.return.fields.source`}
                    sortable={false}
                    textAlign={`left`}
                    render={(record: OrderReturn): any =>
                        SOURCE[record.source] ?? SOURCE.OTHER
                    }
                />
                <TextField source="number" label={`resources.order.return.fields.number`} sortable={true}/>
                <DateField
                    source="createdAt"
                    label={`resources.order.return.fields.createdAt`}
                    locales="de-DE"
                    options={{
                        weekday: undefined,
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                    }}
                    sortable={true}
                />
                <FunctionField
                    sortable={true}
                    source="status"
                    label={`resources.order.return.fields.status`}
                    render={() => <StatusChip/>}
                />
                <BooleanField
                    looseValue={true}
                    source="isFullReturn"
                    sortable={true}
                    label={`resources.order.return.fields.isFullReturn`}
                />
                <TextField source="itemsToReturn" label={`resources.order.return.fields.itemsToReturn`}
                           sortable={true}/>
                <TextField source="carrier" label={`resources.order.return.fields.carrier`}
                           sortable={false}/>
                <TextField source="selectLineDocumentId" label={`resources.order.return.fields.selectLineDocumentId`}
                           sortable={true}/>
                <TextField source="reason" label={`resources.order.return.items.reasonDefaultDescription`}
                           sortable={true}/>
                <DateField
                    source="returnDate"
                    label={`resources.order.return.fields.returnDate`}
                    locales="de-DE"
                    options={{
                        weekday: undefined,
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                    }}
                    sortable={true}
                />
                <BooleanField
                    looseValue={true}
                    source="shippingReturn"
                    sortable={true}
                    label={`resources.order.return.fields.shippingReturn`}
                />
            </DatagridConfigurable>
        </List>
    );
};

export default OrderReturnList;
